import { formatDate } from '@/lib/date-time';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeft, Building2, Mail, MapPin, Phone, User } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '@/lib/api-client';
import { PageShell, StatusPill, type StatusPillProps } from '@/components/shell';
import { SCHEME_LABELS } from '@zhcb/shared';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/clients/$clientId')({
  component: ClientDetailPage,
});

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface Client {
  id: number;
  clientCode: string;
  name: string;
  scheme: string;
  taxId: string | null;
  representativeName: string | null;
  representativeIdNo: string | null;
  representativeMobile: string | null;
  representativeEmail: string | null;
  personInChargeName: string | null;
  personInChargePhone: string | null;
  personInChargeEmail: string | null;
  registeredAddress: string | null;
  mailingAddress: string | null;
  verificationStatus: string;
  createdAt: string;
  updatedAt: string;
}

interface Land {
  id: number;
  parcelNo: string;
  address: string;
  area: number | null;
  cropType: string | null;
}

interface ProcessingSite {
  id: number;
  name: string;
  address: string;
  facilityType: string | null;
}

interface CaseItem {
  id: number;
  caseCode: string;
  verificationType: string;
  workflowState: string;
  createdAt: string;
}

// ─── 狀態顏色映射 ─────────────────────────────────────────────────────────────

const STATE_TONE: Record<string, StatusPillProps['tone']> = {
  受理建檔: 'info',
  合約已簽: 'info',
  已報價: 'info',
  已收費: 'success',
  已派案: 'brand',
  稽核中: 'warning',
  報告中: 'brand',
  報告評審: 'brand',
  決定中: 'warning',
  已發證: 'success',
  已寄送: 'success',
  結案: 'muted',
  NC整改中: 'danger',
};

const VS_TONE: Record<string, StatusPillProps['tone']> = {
  新申請: 'info',
  維持: 'success',
  結束驗證: 'muted',
  撤銷驗證: 'danger',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 客戶詳情頁面主組件。
 */
function ClientDetailPage() {
  const navigate = useNavigate();
  const { clientId } = Route.useParams();

  const { data, isLoading, isError } = useQuery<{ item: Client; lands: Land[]; processingSites: ProcessingSite[] }>({
    queryKey: ['client-detail', clientId],
    queryFn: () => apiClient.get(`/api/settings/clients/${clientId}`),
  });

  const { data: casesData } = useQuery<{ items: CaseItem[] }>({
    queryKey: ['client-cases', clientId],
    queryFn: () => apiClient.get(`/api/clients/${clientId}/cases`),
  });

  const client = data?.item;
  const lands = data?.lands ?? [];
  const processingSites = data?.processingSites ?? [];
  const cases = casesData?.items ?? [];

  if (isLoading)
    return (
      <div className="p-6">
        <Typography.Text>載入中…</Typography.Text>
      </div>
    );
  if (isError || !client)
    return (
      <div className="p-6">
        <Alert type="error" message="客戶不存在" />
      </div>
    );

  const caseColumns: ColumnsType<CaseItem> = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      width: 180,
      render: (code: string, record) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: 'monospace', padding: 0 }}
          onClick={() =>
            void navigate({ to: '/dispatch/$caseId', params: { caseId: String(record.id) } })
          }
        >
          {code}
        </Button>
      ),
    },
    { title: '驗證類型', dataIndex: 'verificationType', width: 160 },
    {
      title: '流程狀態',
      dataIndex: 'workflowState',
      width: 120,
      render: (s: string) => (
        <StatusPill tone={STATE_TONE[s] ?? 'neutral'} dot size="sm">
          {s}
        </StatusPill>
      ),
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 180,
      render: (d: string) => formatDate(d),
    },
  ];

  const landColumns: ColumnsType<Land> = [
    { title: '地號', dataIndex: 'parcelNo', width: 180 },
    { title: '地址', dataIndex: 'address' },
    {
      title: '面積 (公頃)',
      dataIndex: 'area',
      width: 120,
      render: (a: number | null) => (a !== null ? a.toFixed(2) : '—'),
    },
    { title: '作物類型', dataIndex: 'cropType', width: 120, render: (v) => v ?? '—' },
  ];

  const siteColumns: ColumnsType<ProcessingSite> = [
    { title: '場所名稱', dataIndex: 'name', width: 200 },
    { title: '地址', dataIndex: 'address' },
    { title: '設施類型', dataIndex: 'facilityType', width: 120, render: (v) => v ?? '—' },
  ];

  return (
    <PageShell
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Building2 size={20} />
          {client.name}
        </span>
      }
      description={
        <Space size="small">
          {client.taxId && <span>統編 {client.taxId}</span>}
          <span>・</span>
          <span>{(SCHEME_LABELS as Record<string, string>)[client.scheme] ?? client.scheme}</span>
        </Space>
      }
      meta={
        <>
          <Tag color="blue" style={{ fontFamily: 'monospace' }}>
            {client.clientCode}
          </Tag>
          <StatusPill tone={VS_TONE[client.verificationStatus] ?? 'neutral'} dot>
            {client.verificationStatus}
          </StatusPill>
        </>
      }
      actions={
        <Space>
          <Button
            onClick={() => void navigate({ to: '/settings/clients' })}
            icon={<ArrowLeft size={14} />}
          >
            返回列表
          </Button>
        </Space>
      }
    >
      {/* 基本資料 */}
      <Card className="mb-4" size="small" title={<><User size={16} className="mr-2" />基本資料</>}>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="客戶名稱">{client.name}</Descriptions.Item>
              <Descriptions.Item label="驗證註冊碼">
                <Typography.Text code>{client.clientCode}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="驗證方案">
                {(SCHEME_LABELS as Record<string, string>)[client.scheme] ?? client.scheme}
              </Descriptions.Item>
              <Descriptions.Item label="統一編號">{client.taxId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="驗證狀態">
                <StatusPill tone={VS_TONE[client.verificationStatus] ?? 'neutral'} dot size="sm">
                  {client.verificationStatus}
                </StatusPill>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="建檔時間">{formatDate(client.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="最後更新">{formatDate(client.updatedAt)}</Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* 聯絡資料 */}
      <Card
        className="mb-4"
        size="small"
        title={<><Phone size={16} className="mr-2" />聯絡資料</>}
      >
        <Row gutter={[24, 16]}>
          {/* 法人 */}
          <Col xs={24} md={12}>
            <Typography.Text strong className="block mb-2">法人代表</Typography.Text>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="姓名">{client.representativeName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="身份證號">{client.representativeIdNo ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={<><Phone size={12} className="mr-1" />手機</>}>
                {client.representativeMobile ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><Mail size={12} className="mr-1" />Email</>}>
                {client.representativeEmail ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          {/* 負責人 */}
          <Col xs={24} md={12}>
            <Typography.Text strong className="block mb-2">負責人</Typography.Text>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="姓名">{client.personInChargeName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={<><Phone size={12} className="mr-1" />電話</>}>
                {client.personInChargePhone ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label={<><Mail size={12} className="mr-1" />Email</>}>
                {client.personInChargeEmail ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* 地址資料 */}
      <Card
        className="mb-4"
        size="small"
        title={<><MapPin size={16} className="mr-2" />地址資料</>}
      >
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="註冊地址">{client.registeredAddress ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="通訊地址">{client.mailingAddress ?? '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      {/* 詳情 Tabs */}
      <Card>
        <Tabs
          items={[
            {
              key: 'cases',
              label: `驗證案件 (${cases.length})`,
              children: (
                <Table<CaseItem>
                  dataSource={cases}
                  rowKey="id"
                  size="small"
                  columns={caseColumns}
                  pagination={cases.length > 10 ? { pageSize: 10 } : false}
                />
              ),
            },
            {
              key: 'lands',
              label: `地塊 (${lands.length})`,
              children: (
                <Table<Land>
                  dataSource={lands}
                  rowKey="id"
                  size="small"
                  columns={landColumns}
                  pagination={lands.length > 10 ? { pageSize: 10 } : false}
                  locale={{ emptyText: '暫無地塊資料' }}
                />
              ),
            },
            {
              key: 'sites',
              label: `加工場 (${processingSites.length})`,
              children: (
                <Table<ProcessingSite>
                  dataSource={processingSites}
                  rowKey="id"
                  size="small"
                  columns={siteColumns}
                  pagination={processingSites.length > 10 ? { pageSize: 10 } : false}
                  locale={{ emptyText: '暫無加工場資料' }}
                />
              ),
            },
          ]}
        />
      </Card>
    </PageShell>
  );
}
